import React, { useState, useEffect } from 'react';
import { Star, Plus, Edit, Trash2, X, Upload, Check } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    rating: 5,
    review_text: '',
    customer_image: '',
    product_images: ['', '', ''],
    is_featured: false
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/testimonials/?approved_only=false`);
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (index, file, isCustomer = false) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/products/upload-image`, formDataUpload);
      
      if (isCustomer) {
        setFormData({ ...formData, customer_image: response.data.url });
      } else {
        const newImages = [...formData.product_images];
        newImages[index] = response.data.url;
        setFormData({ ...formData, product_images: newImages });
      }
      
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.review_text) {
      toast.error('Please fill in required fields');
      return;
    }

    const cleanedData = {
      ...formData,
      product_images: formData.product_images.filter(img => img.trim() !== '')
    };

    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, cleanedData);
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/testimonials/`, cleanedData);
        toast.success('Testimonial created!');
      }
      
      fetchTestimonials();
      closeModal();
    } catch (error) {
      toast.error('Failed to save testimonial');
      console.error(error);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
      console.error(error);
    }
  };

  const toggleApproval = async (testimonial) => {
    try {
      await axios.put(`${API}/testimonials/${testimonial.id}`, {
        is_approved: !testimonial.is_approved
      });
      toast.success(testimonial.is_approved ? 'Testimonial hidden' : 'Testimonial approved!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        customer_name: testimonial.customer_name,
        rating: testimonial.rating,
        review_text: testimonial.review_text,
        customer_image: testimonial.customer_image || '',
        product_images: [...testimonial.product_images, '', '', ''].slice(0, 3),
        is_featured: testimonial.is_featured
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        customer_name: '',
        rating: 5,
        review_text: '',
        customer_image: '',
        product_images: ['', '', ''],
        is_featured: false
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? 'star-filled' : 'star-empty'}
        fill={index < rating ? 'currentColor' : 'none'}
      />
    ));
  };

  return (
    <div className="testimonials-admin">
      <div className="admin-header">
        <h2 className="admin-title">Customer Testimonials</h2>
        <button className="btn-primary" onClick={() => openModal()}>
          <Plus size={20} />
          Add Testimonial
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading testimonials...</div>
      ) : (
        <div className="testimonials-grid">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className={`testimonial-admin-card ${!testimonial.is_approved ? 'inactive' : ''}`}>
              <CardContent className="testimonial-admin-content">
                <div className="testimonial-admin-header">
                  <div className="customer-info-admin">
                    {testimonial.customer_image && (
                      <img src={testimonial.customer_image} alt={testimonial.customer_name} className="customer-avatar-small" />
                    )}
                    <div>
                      <h4>{testimonial.customer_name}</h4>
                      <div className="rating-stars">{renderStars(testimonial.rating)}</div>
                    </div>
                  </div>
                  <div className="testimonial-admin-actions">
                    <button 
                      className={`btn-icon ${testimonial.is_approved ? '' : 'inactive'}`}
                      onClick={() => toggleApproval(testimonial)}
                      title={testimonial.is_approved ? 'Hide' : 'Approve'}
                    >
                      <Check size={18} />
                    </button>
                    <button className="btn-icon" onClick={() => openModal(testimonial)}>
                      <Edit size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(testimonial.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <p className="review-text-admin">{testimonial.review_text}</p>
                {testimonial.product_images.length > 0 && (
                  <div className="product-images-preview-small">
                    {testimonial.product_images.map((img, idx) => (
                      <img key={idx} src={img} alt="Product" />
                    ))}
                  </div>
                )}
                {testimonial.is_featured && <span className="featured-badge">Featured</span>}
                {!testimonial.is_approved && <span className="inactive-badge">Hidden</span>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Rating *</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                  >
                    {[5, 4, 3, 2, 1].map(num => (
                      <option key={num} value={num}>{num} Stars</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Review Text *</label>
                <textarea
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                  {' '}Featured Testimonial
                </label>
              </div>

              <div className="form-group">
                <label>Customer Photo (Optional)</label>
                {formData.customer_image ? (
                  <div className="image-preview">
                    <img src={formData.customer_image} alt="Customer" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => setFormData({ ...formData, customer_image: '' })}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(0, e.target.files[0], true)}
                      style={{ display: 'none' }}
                    />
                    <Upload size={24} />
                    <span>Upload Photo</span>
                  </label>
                )}
              </div>

              <div className="form-group">
                <label>Product Usage Photos (Up to 3)</label>
                <div className="images-grid">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="image-upload-box">
                      {formData.product_images[index] ? (
                        <div className="image-preview">
                          <img src={formData.product_images[index]} alt={`Product ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => {
                              const newImages = [...formData.product_images];
                              newImages[index] = '';
                              setFormData({ ...formData, product_images: newImages });
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                            style={{ display: 'none' }}
                          />
                          <Upload size={24} />
                          <span>Photo {index + 1}</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTestimonial ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdmin;